package com.macrofocus.eflux.ui.data

import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.ui.data.risk.*
import mui.icons.material.QuestionMarkTwoTone
import mui.material.*
import mui.material.styles.Theme
import mui.material.styles.useTheme
import mui.system.sx
import org.mkui.color.MkColor
import org.mkui.color.brighter
import org.mkui.color.toKotlinJS
import org.mkui.react.useProperty
import react.*
import react.dom.html.ReactHTML.span
import web.cssom.FontFamily
import web.cssom.FontSize
import web.cssom.None
import web.cssom.WhiteSpace
import web.timers.Timeout
import web.timers.clearTimeout
import web.timers.setTimeout
import kotlin.time.Duration.Companion.milliseconds

external interface LineButtonProps : Props {
    var dataLoader: DataLoader
    var primary: Boolean?
    var probing: MutableSingleSelection<LogicalBranchId>?
    var selection: MutableSelection<LogicalBranchId>?
    var logicalBranchId: LogicalBranchId
    var filled: Boolean?
    var risks: Boolean?
}

val LineButton = FC<LineButtonProps> { props ->
    val outlined = true
    val theme = useTheme<Theme>()

    val dataLoader = props.dataLoader
    val controller = dataLoader.controller
    val logicalBranchId = props.logicalBranchId

    val (network, _) = useProperty(dataLoader.substationNetwork)
    val line = network?.lines[logicalBranchId]
    val (fullName, _) = useProperty(dataLoader.fullName)

    val clickTimer = useRef<Timeout>(null)

    if(line != null) {
        Tooltip {
            title = if(!fullName) ReactNode(line.longName ?: line.logicalBranchId.name) else ReactNode(line.logicalBranchId.name)
            placement = TooltipPlacement.left

            Badge {
                if(props.risks == true && line.assetCount != null) {
                    badgeContent = ReactNode(line.assetCount.toString())
                    val probability = line.probability
                    color = if (probability != null) {
                        if (probability >= 0.1) BadgeColor.error
                        else if (probability >= 0.05) BadgeColor.warning
                        else if (probability > 0.0) BadgeColor.info
                        else BadgeColor.default
                    } else BadgeColor.default
                }

                Button {
                    sx {
                        if(fullName) {
                            textTransform = None.none
                        } else {
                            fontFamily = "'JetBrains Mono', monospace".unsafeCast<FontFamily>()
                            fontSize = FontSize.xSmall
                        }
                        if(!outlined) {
                            backgroundColor = dataLoader.getColor(line, theme).toKotlinJS()
                            "&:hover" {
                                backgroundColor = dataLoader.getColor(line, theme).brighter().toKotlinJS()
                            }
                        } else {
                            if(!(props.filled == false)) {
                                backgroundColor = (theme.asDynamic().substationFillColor as MkColor).toKotlinJS()
                            }
                            borderColor = dataLoader.getColor(line, theme).toKotlinJS()
                            "&:hover" {
                                borderColor = dataLoader.getColor(line, theme).brighter().toKotlinJS()
                            }

                        }
                    }

                    val riskMap = line.riskMap
                    val probability = line.probability
                    if(props.risks == true && riskMap != null && riskMap > 0) {
                        startIcon = if (riskMap and TemperatureBit != 0) {
                            TemperatureIcon.create {
                                color = if (probability != null) {
                                    if (probability >= 0.1) SvgIconColor.error
                                    else if (probability >= 0.05) SvgIconColor.warning
                                    else if (probability > 0.0) SvgIconColor.info
                                    else SvgIconColor.disabled
                                } else SvgIconColor.disabled
                            }
                        } else if (riskMap and WindGustsBit != 0) WindGustsIcon.create {
                            color = if (probability != null) {
                                if (probability >= 0.1) SvgIconColor.error
                                else if (probability >= 0.05) SvgIconColor.warning
                                else if (probability > 0.0) SvgIconColor.info
                                else SvgIconColor.disabled
                            } else SvgIconColor.disabled
                        } else if (riskMap and MudslideBit != 0) MudslideIcon.create {
                            color = if (probability != null) {
                                if (probability >= 0.1) SvgIconColor.error
                                else if (probability >= 0.05) SvgIconColor.warning
                                else if (probability > 0.0) SvgIconColor.info
                                else SvgIconColor.disabled
                            } else SvgIconColor.disabled
                        } else if (riskMap and FloodingBit != 0) FloodingIcon.create {
                            color = if (probability != null) {
                                if (probability >= 0.1) SvgIconColor.error
                                else if (probability >= 0.05) SvgIconColor.warning
                                else if (probability > 0.0) SvgIconColor.info
                                else SvgIconColor.disabled
                            } else SvgIconColor.disabled
                        } else if (riskMap and LightningExpectedBit != 0) LightningExpectedIcon.create {
                            color = if (probability != null) {
                                if (probability >= 0.1) SvgIconColor.error
                                else if (probability >= 0.05) SvgIconColor.warning
                                else if (probability > 0.0) SvgIconColor.info
                                else SvgIconColor.disabled
                            } else SvgIconColor.disabled
                        } else if (riskMap and LightningActivityBit != 0) LightningActivityIcon.create {
                            color = if (probability != null) {
                                if (probability >= 0.1) SvgIconColor.error
                                else if (probability >= 0.05) SvgIconColor.warning
                                else if (probability > 0.0) SvgIconColor.info
                                else SvgIconColor.disabled
                            } else SvgIconColor.disabled
                        } else if (riskMap and LightningHitBit != 0) LightningHitIcon.create {
                            color = if (probability != null) {
                                if (probability >= 0.1) SvgIconColor.error
                                else if (probability >= 0.05) SvgIconColor.warning
                                else if (probability > 0.0) SvgIconColor.info
                                else SvgIconColor.disabled
                            } else SvgIconColor.disabled
                        } else QuestionMarkTwoTone.create {
                            color = if (probability != null) {
                                if (probability >= 0.1) SvgIconColor.error
                                else if (probability >= 0.05) SvgIconColor.warning
                                else if (probability > 0.0) SvgIconColor.info
                                else SvgIconColor.disabled
                            } else SvgIconColor.disabled
                        }
                    }

                    variant = ButtonVariant.outlined
                    size = Size.small
                    onPointerEnter = {
                        if(props.primary != null) controller.probeLine(line.logicalBranchId, props.primary!!)
                    }
                    onPointerLeave = {
                        if(props.primary != null) controller.probeLine(null, props.primary!!)
                    }
                    onClick = {
                        // cancel previous timer
                        clickTimer.current?.let {
                            clearTimeout(it)
                        }

                        // start new one
                        clickTimer.current = setTimeout(250.milliseconds) {
                            dataLoader.overviewZoom(line.logicalGeometry, line.physicalGeometry)
                            if(props.primary != null) controller.selectLine(line.logicalBranchId, props.primary!!)
                        }
                    }
                    onDoubleClick = {
                        // cancel pending single click
                        clickTimer.current?.let { clearTimeout(it) }

                        dataLoader.physicalZoom(line.physicalGeometry)
                        if(props.primary != null) controller.selectLine(line.logicalBranchId, props.primary!!)
                    }
                    Box {
                        component = span
                        sx {
//                            display = Display.inlineBlock
//                            overflow = Overflow.hidden
//                            textOverflow = TextOverflow.ellipsis
                            whiteSpace = WhiteSpace.nowrap
//                            maxWidth = 100.pct
                        }
                        +if(fullName) line.longName ?: line.logicalBranchId.name else line.logicalBranchId.name
                    }
                }
            }
        }
    }
}